import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Spinner } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import {
  FileText, ShoppingCart, PackagePlus, Wrench, AlertTriangle, CheckCircle2,
  Clock, Layers, Cpu, ChevronRight, ListChecks,
} from "lucide-react";

function MiniKpi({ label, value, icon: Icon, accent, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="neon-card flex items-center gap-3 px-3 py-2.5 text-left w-full group"
    >
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border ${accent ? "border-[rgba(255,120,150,0.5)] bg-[rgba(255,60,90,0.12)]" : "border-[rgba(0,200,255,0.4)] bg-[rgba(0,180,255,0.08)]"}`}>
        {Icon && <Icon size={16} className={accent ? "text-[#FF8CA3]" : "icon-cyan"} strokeWidth={2.2} />}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500 truncate">{label}</div>
        <div className={`font-head text-xl font-extrabold leading-none ${accent ? "text-[#FF8CA3]" : "text-white"}`}>{value}</div>
      </div>
    </button>
  );
}

const ACTION_TONE = {
  Order: { bg: "rgba(0,180,255,0.14)", color: "#5CC8FF", border: "rgba(0,200,255,0.5)" },
  "Terima Part": { bg: "rgba(255,190,0,0.14)", color: "#FFD65C", border: "rgba(255,200,0,0.5)" },
  Pasang: { bg: "rgba(0,220,130,0.14)", color: "#5EEBB0", border: "rgba(0,220,130,0.5)" },
  History: { bg: "rgba(120,160,210,0.12)", color: "#9DB6D6", border: "rgba(120,160,210,0.4)" },
};

function ActionBtn({ action, role, onGo }) {
  const allowed = action.roles.includes(role) || role === "owner";
  const t = ACTION_TONE[action.label] || ACTION_TONE.History;
  return (
    <button
      disabled={!allowed}
      onClick={(e) => { e.stopPropagation(); if (allowed) onGo(action.to); }}
      data-testid={`action-${action.label}`}
      className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }}
      title={allowed ? "" : "Tidak ada akses untuk role ini"}
    >
      {action.label}<ChevronRight size={12} />
    </button>
  );
}

function NeedActionTable({ rows, role, navigate }) {
  return (
    <Card className="overflow-hidden" data-testid="need-action-table">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[rgba(0,200,255,0.15)]">
        <ListChecks size={15} className="icon-cyan" />
        <h3 className="font-head font-semibold text-white text-sm tracking-wide">NEED ACTION TODAY</h3>
        <span className="text-[11px] text-gray-500">{rows.length} kendaraan</span>
      </div>
      {rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500" data-testid="need-action-empty">
          Semua kendaraan sudah tertangani. Tidak ada aksi tertunda. 🎉
        </div>
      ) : (
        <div className="overflow-x-auto" style={{ maxHeight: "calc(100vh - 320px)" }}>
          <table className="w-full text-xs">
            <thead className="bg-[rgba(0,180,255,0.05)] text-gray-500 uppercase sticky top-0 backdrop-blur">
              <tr>
                <th className="text-left px-3 py-2">Nopol</th>
                <th className="text-left px-3 py-2">Mobil</th>
                <th className="text-left px-3 py-2">SPK / Claim</th>
                <th className="text-left px-3 py-2">Status Part</th>
                <th className="text-left px-3 py-2 w-32">Progress</th>
                <th className="text-left px-3 py-2">Issue</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.job_id}
                  className="border-t border-[rgba(0,200,255,0.08)] hover:bg-[rgba(0,200,255,0.05)] cursor-pointer"
                  onClick={() => navigate(`/history?job=${r.job_id}`)}
                  data-testid={`need-action-row-${r.job_id}`}
                >
                  <td className="px-3 py-2 font-mono-data font-semibold text-white">{r.nopol}</td>
                  <td className="px-3 py-2 text-gray-400">{r.vehicle || "—"}</td>
                  <td className="px-3 py-2 font-mono-data text-gray-400">{r.spk || "—"}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.status_part === "COMPLETE" ? "PARTS_COMPLETE" : r.status_part === "PARTIAL" ? "PARTS_PARTIAL" : "PARTS_WAITING"} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-[rgba(120,160,210,0.15)] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${r.progress}%`, background: r.progress >= 100 ? "#5EEBB0" : "#5CC8FF", boxShadow: "0 0 6px currentColor" }} />
                      </div>
                      <span className="text-[10px] text-gray-500 tabular-nums w-14 text-right">{r.received}/{r.required}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">{r.issue ? <StatusBadge status={r.issue} /> : <span className="text-gray-600">—</span>}</td>
                  <td className="px-3 py-2 text-right"><ActionBtn action={r.action} role={role} onGo={navigate} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState("");
  const [needAction, setNeedAction] = useState([]);
  const [loading, setLoading] = useState(true);

  const kind = user.role === "owner" || user.role === "viewer" ? "owner"
    : user.role === "partman" ? "partman" : "warehouse";

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/${kind}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get("/dashboard/need-action").then((r) => setNeedAction(r.data)).catch(() => {});
    if (kind === "owner" || kind === "partman") {
      api.get("/dashboard/ai-summary").then((r) => setSummary(r.data.summary)).catch(() => {});
    }
  }, [kind]);

  if (loading) return <Spinner />;
  const d = data || {};

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h1 className="font-head text-xl sm:text-2xl font-bold tracking-tight text-white glow-text">Halo, {user.name.split(" ")[0]}</h1>
          <p className="text-gray-500 text-xs">{user.role_label} — {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        {(kind === "owner" || kind === "partman") && summary && (
          <div className="flex items-center gap-2 rounded-lg border border-[rgba(0,200,255,0.25)] bg-[rgba(0,180,255,0.05)] px-3 py-1.5 max-w-xl" data-testid="ai-summary-panel">
            <Cpu size={14} className="icon-cyan shrink-0" />
            <span className="text-[11px] text-gray-400 leading-snug line-clamp-2" data-testid="ai-summary">{summary}</span>
          </div>
        )}
      </div>

      {kind === "owner" && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
          <MiniKpi label="Pesan" value={d.part_pesan_today ?? 0} icon={ShoppingCart} testId="stat-pesan" onClick={() => navigate("/part-pesan")} />
          <MiniKpi label="Datang" value={d.part_datang_today ?? 0} icon={PackagePlus} testId="stat-datang" onClick={() => navigate("/part-masuk")} />
          <MiniKpi label="Complete" value={d.part_komplit ?? 0} icon={CheckCircle2} testId="stat-komplit" onClick={() => navigate("/part-komplit")} />
          <MiniKpi label="WH Reserved" value={d.wh_reserved ?? 0} icon={Layers} testId="stat-reserved" onClick={() => navigate("/warehouse")} />
          <MiniKpi label="Overdue" value={d.overdue_orders ?? 0} icon={AlertTriangle} accent testId="stat-overdue" onClick={() => navigate("/part-overdue")} />
          <MiniKpi label="Unmatched" value={d.unmatched ?? 0} icon={AlertTriangle} accent testId="stat-unmatched" onClick={() => navigate("/unmatched")} />
        </div>
      )}

      {kind === "partman" && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
          <MiniKpi label="SPK Hari Ini" value={d.spk_uploaded_today ?? 0} icon={FileText} onClick={() => navigate("/spk")} />
          <MiniKpi label="Pesan" value={d.part_pesan_today ?? 0} icon={ShoppingCart} onClick={() => navigate("/part-pesan")} />
          <MiniKpi label="Waiting" value={d.parts_waiting ?? 0} icon={Clock} onClick={() => navigate("/jobs")} />
          <MiniKpi label="Complete" value={d.parts_complete ?? 0} icon={CheckCircle2} onClick={() => navigate("/jobs")} />
          <MiniKpi label="Overdue" value={d.overdue_orders ?? 0} icon={AlertTriangle} accent />
          <MiniKpi label="Unmatched" value={d.unmatched ?? 0} icon={AlertTriangle} accent onClick={() => navigate("/unmatched")} />
        </div>
      )}

      {kind === "warehouse" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <MiniKpi label="Datang Hari Ini" value={d.part_datang_today ?? 0} icon={PackagePlus} onClick={() => navigate("/part-masuk")} />
          <MiniKpi label="Terpasang Hari Ini" value={d.installed_today ?? 0} icon={Wrench} onClick={() => navigate("/part-terpasang")} />
          <MiniKpi label="General Stock" value={d.general_stock ?? 0} icon={Layers} onClick={() => navigate("/warehouse")} />
          <MiniKpi label="Unmatched" value={d.unmatched ?? 0} icon={AlertTriangle} accent />
        </div>
      )}

      <NeedActionTable rows={needAction} role={user.role} navigate={navigate} />
    </div>
  );
}
